import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProgressSlider } from "../../../shared/progress-slider";
import useAudioPlayer from "./hooks/useAudioPlayer";
import { ProgressSliderApi } from "../../../shared/progress-slider/view";
import useSongStore, { LoopMode } from "../store/SongState";
import { isAppleDevice, isMobileDevice } from "../../../../utils/device-utils";
import { DiscordBotApi } from "../../../../utils/discordBotApi";
import UseVolumeSlider from "./hooks/useVolumeSlider";
import { handleCopy } from "../../../../utils/utils";
import { ScrollModalApi } from "../../../shared/scroll-modal/view";

import { SongQueueScrollModal } from "./song-queue-scroll-modal";
import SquareImage from "../../../shared/square-image/square-image";
import { getSongImageUrl, Spotip17anFunctions } from "../../../../utils/api";
import useUserStore from "../../../../store/UserState";
import toast from "react-hot-toast";
import { SongOptionsModal } from "../song-list/song-options-modal";
import { Song } from "../types";
import classNames from "classnames";
import LocalStorage from "../../../../utils/local-storage";
import YoutubeIframe, { YoutubeIframeRef } from "../../../shared/youtube-iframe/youtube-iframe";
import { useResolvedTrack, useScreenSize, useTrackCanvas } from "../../../../hooks";

import './styles.scss';
import '../../spotipitan-content/desktop-right-sidebar/desktop-right-sidebar.scss';
import auth from "../../../../firebase/auth";
import { ENABLE_VIDEO_IN_SIDEBAR } from "../../spotipitan-content/desktop-right-sidebar/desktop-right-sidebar";

const defaultIcon = "https://torresowo.myftp.org:8082/files/cd812ccb405099877fe1964a4ba6cf2d";

const AudioPlayerView = () => {
    const { state, setRandomQueue, setLoopMode, setQueue, setOriginalQueueOrder } = useSongStore(state => state);
    const { currentSong, songQueue, randomQueue, loopMode, originalQueueOrder } = state;
    const { canvasUrl } = useTrackCanvas(currentSong);
    const { title: songTitle, artistText: songArtist } = useResolvedTrack(currentSong);
    const audioRef = useRef<HTMLAudioElement>(null);
    const controlsRef = useRef<HTMLDivElement>(null);
    const timeSlider = useRef<ProgressSliderApi>(null);
    const songPlayerRef = useRef<HTMLDivElement>(null);
    const songPlayerInfoRef = useRef<HTMLDivElement>(null);
    const songQueueModalRef = useRef<ScrollModalApi>(null);
    const maximized = useRef(false)
    const [isFullScreen, setIsFullScreen] = React.useState(false);
    const loading = useRef(false);
    const startY = useRef(0);
    const lastY = useRef(0);
    const endY = useRef(0);
    const isSwipeBottom = useRef(false);
    const volumeIconRef = useRef<HTMLDivElement>(null);
    const volumeSliderRef = useRef<ProgressSliderApi>(null);
    const initialBottom = useRef("0px");
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasVideoRef = useRef<HTMLVideoElement>(null);
    const [showVideo, setShowVideo] = React.useState(false);
    const [videoBlobUrl, setVideoBlobUrl] = React.useState<string | null>(null);
    const lastVideoId = useRef<string | null>(null);
    const [videoBlobError, setVideoBlobError] = React.useState(false);
    const { user, offlineMode } = useUserStore(state => state.state);
    const [isFavourite, setIsFavourite] = React.useState<boolean | undefined>(undefined);
    const songOptionsModalRef = useRef<ScrollModalApi>(null);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const youtubePlayerRef = useRef<YoutubeIframeRef>(null);
    const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastSyncTime = useRef<number>(0);
    const [syncEnabled, setSyncEnabled] = useState(true);
    const [documentFullScreen, setDocumentFullScreen] = useState(false);
    const lastSongIdRef = useRef<string | number | undefined>(undefined);
    const { width } = useScreenSize();

    useEffect(() => {
        setShowVideo(LocalStorage.getItem("showVideo") === "true");

        const handleShowVideoChanged = (e: Event) => {
            const customEvent = e as CustomEvent;
            setShowVideo(customEvent.detail);
        };
        window.addEventListener("showVideoChanged", handleShowVideoChanged);
        return () => {
            window.removeEventListener("showVideoChanged", handleShowVideoChanged);
        };
    }, []);

    const handleSetShowVideo = (value: boolean) => {
        setShowVideo(value);
        LocalStorage.setItem("showVideo", value ? "true" : "false");
        window.dispatchEvent(new CustomEvent("showVideoChanged", { detail: value }));
    }

    const handleToggleFullscreen = useCallback(() => {
        if (!showVideo) return;
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            const poster = songPlayerRef.current?.requestFullscreen();
        }
    }, [showVideo]);

    // Add an event listener for fullscreenchange
    useEffect(() => {
        const handleFullscreenChange = () => {
            setDocumentFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const { handleVolumeChange } = UseVolumeSlider({ volumeSliderRef, volumeIconRef, audioRef });

    const maximizeSongPlayer = () => {
        if (maximized.current || loading.current || width >= 1200) return;
        loading.current = true;
        setTimeout(() => {
            loading.current = false;
        }, 500);
        maximized.current = true;
        songPlayerRef.current!.classList.add('song-player-expanded');
        songPlayerInfoRef.current!.classList.add('player-info-expanded');
        document.body.style.overflowY = "hidden"

        document.addEventListener('touchmove', handleSwipeDown);
        initialBottom.current = songPlayerRef.current!.style.bottom;
        songPlayerRef.current!.style.bottom = "0";
        setIsFullScreen(true);
    }

    const minimizeSongPlayer = (e?: React.MouseEvent) => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        e?.stopPropagation();
        if (!maximized.current || loading.current) return;
        loading.current = true;
        setTimeout(() => {
            loading.current = false;
        }, 500);
        maximized.current = false;
        songPlayerRef.current!.classList.remove('song-player-expanded');
        songPlayerInfoRef.current!.classList.remove('player-info-expanded')
        document.body.style.overflowY = "auto";
        document.removeEventListener('touchmove', handleSwipeDown);
        songPlayerRef.current!.classList.remove('deanimate-top');
        songPlayerRef.current!.style.bottom = initialBottom.current;
        setIsFullScreen(false);
    }

    const { duration, currentTime, paused, playerControls, audioPlayerMethods, updateSongPlayerTime } = useAudioPlayer({
        audioRef,
        controlsRef,
        progressSlider: timeSlider,
        maximizeSongPlayer,
        isFullScreen,
    });

    useEffect(() => {
        if (!canvasVideoRef.current) return;
        if (paused || document.hidden || !isFullScreen) {
            canvasVideoRef.current.pause();
        } else {
            canvasVideoRef.current.play().catch(() => { });
        }
    }, [paused, canvasUrl, isFullScreen]);

    const handleSetRandomQueue = (value: boolean) => {
        if (state.isBotMode && state.botGuildId) {
            DiscordBotApi.shuffle(state.botGuildId)
                .then(() => toast.success("Cola de Discord mezclada"))
                .catch(e => console.error("Error shuffling bot queue:", e));
            return;
        }
        setRandomQueue(value);
        if (!songQueue.length) return;
        if (value) {
            setOriginalQueueOrder([...songQueue.map((song) => song.id)]);
            const newQueue = [...songQueue];
            newQueue.sort(() => Math.random() - 0.5);
            setQueue(newQueue);
        } else {
            const newQueue = originalQueueOrder
                .map((id) => songQueue.find((song) => song.id === id))
                .filter((song) => !!song) as any as Song[];
            setQueue(newQueue);
        }
    }

    const handleChangeLoopMode = () => {
        let nextMode: LoopMode = "none";
        if (loopMode === "none") {
            nextMode = "queue";
        } else if (loopMode === "queue") {
            nextMode = "song";
        } else {
            nextMode = "none";
        }

        if (state.isBotMode && state.botGuildId) {
            DiscordBotApi.loop(state.botGuildId, nextMode)
                .then(() => {
                    setLoopMode(nextMode);
                    toast.success(`Modo bucle: ${nextMode}`);
                })
                .catch(e => console.error("Error setting loop mode on bot:", e));
            return;
        }

        setLoopMode(nextMode);
    }

    useEffect(() => {
        if (width > 1200) {
            minimizeSongPlayer();
        }
    }, [width])

    useEffect(() => {
        if (!songPlayerRef.current) return;

        const handleTouchStart = (e: TouchEvent) => {
            if (maximized.current) {
                startY.current = Number(e.touches[0].clientY);
                lastY.current = Number(e.touches[0].clientY);
                endY.current = Number(e.touches[0].clientY);
                isSwipeBottom.current = false;
                songPlayerRef.current!.classList.add('deanimate-top');
            }
        }

        const handleTouchEnd = (e: TouchEvent) => {
            startY.current = 0;
            if (isSwipeBottom.current) {
                minimizeSongPlayer();
            } else {
                if (maximized.current && !loading.current) {
                    songPlayerRef.current!.classList.remove('deanimate-top');
                    songPlayerRef.current!.style.bottom = `0`;
                }
            }
        }

        songPlayerRef.current.addEventListener('touchstart', handleTouchStart, { passive: true });
        songPlayerRef.current.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            songPlayerRef.current?.removeEventListener('touchstart', handleTouchStart);
            songPlayerRef.current?.removeEventListener('touchend', handleTouchEnd);
        }
    }, [songPlayerRef.current]);

    const handleToggleFavourite = async () => {
        let currentFav: boolean | undefined = isFavourite;
        if (isFavourite === undefined) {
            currentFav = await Spotip17anFunctions.use('findFavoritesIds').call().then((res: string[]) => {
                const aux = res.includes(currentSong?.id + "");
                setIsFavourite(aux);
                return aux;
            }).catch(() => {
                return false;
            });
        }
        const newValue = !currentFav;

        setIsFavourite(newValue);

        if (newValue) {
            Spotip17anFunctions.use('addToFavorites').call({ songId: currentSong?.id + "" }).then(() => {
                toast.success('Canción añadida a favoritos');
            });
        } else {
            Spotip17anFunctions.use('removeFromFavorites').call({ songId: currentSong?.id + "" }).then(() => {
                toast.success('Canción eliminada de favoritos');
            });
        }
    }

    useEffect(() => {
        if (!currentSong) {
            setIsFavourite(undefined);
            lastSongIdRef.current = undefined;
            return;
        }

        if (currentSong.id === lastSongIdRef.current) {
            return;
        }

        lastSongIdRef.current = currentSong.id;
        setIsFavourite(undefined);

        if (!user?.uid) return;

        Spotip17anFunctions.use('findFavoritesIds').call().then((res: string[]) => {
            setIsFavourite(res.includes(currentSong?.id + ""));
        }).catch(() => {
        });
    }, [currentSong, user]);

    const songHasVideo = useMemo(() => {
        return !state.isBotMode && !!currentSong && isNaN(currentSong.id);
    }, [currentSong, state.isBotMode]);

    const hasCanvas = useMemo(() => (songHasVideo || !!canvasUrl) && width <= 1200, [songHasVideo, canvasUrl, width]);

    useEffect(() => {
        if (lastVideoId.current === currentSong?.id) {
            return;
        }

        setVideoBlobError(false);

        if (!(user?.uid && (showVideo || hasCanvas) && currentSong)) {
            return;
        }

        lastVideoId.current = currentSong.id;
        setVideoBlobUrl("ah");

    }, [showVideo, currentSong, songHasVideo, isFullScreen, user, hasCanvas]);

    const handleClickSongPlayer = (e: React.MouseEvent) => {
        if (isMobileDevice()) {
            maximizeSongPlayer();
        }
    }

    const handleOpenQueueModal = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (songQueueModalRef.current?.isOpen) {
            songQueueModalRef.current?.close();
            return;
        }
        songQueueModalRef.current?.open({
            y: e.clientY,
            x: e.clientX
        });
    }

    const handleSwipeDown = (e: TouchEvent) => {
        e.stopPropagation();
        if (maximized.current && !loading.current && startY.current > 0) {
            if (timeSlider.current?.isSliding() || volumeSliderRef.current?.isSliding()) return;

            songPlayerRef.current!.classList.add('deanimate-top');
            endY.current = Number(e.touches[0].clientY);
            songPlayerRef.current!.style.bottom = `${Math.min(0, 0 - (endY.current - startY.current))}px`;
            isSwipeBottom.current = endY.current > lastY.current || (endY.current === lastY.current && isSwipeBottom.current);
            lastY.current = Number(endY.current);
        }
    };

    const handleChangeTime = (progress: number, sliding?: boolean) => {
        if (state.isBotMode) {
            if (!sliding) {
                toast.error("No se puede cambiar el progreso en Modo Bot");
            }
            return;
        }
        if (!audioRef.current) return;
        updateSongPlayerTime(progress / 1000, audioRef.current.duration, true);
        if (!sliding) {
            audioRef.current.currentTime = progress / 1000;
            if (videoRef.current) {
                videoRef.current.currentTime = progress / 1000;
            }
        }
    }

    function handleCopyLink() {
        if (currentSong) {
            handleCopy('https://spotip17an.es/share?song=' + currentSong.id);
        }
    }

    const handleAudioOnEnded = () => {
        audioPlayerMethods.onEnded();
    }

    const handleAudioError = () => {
        const audio = audioRef.current;
        const currentSrc = audio?.currentSrc || audio?.src;

        // Ignore uninitialized or empty audio element
        if (!currentSrc || currentSrc === "" || currentSrc === window.location.href) {
            return;
        }

        // Ignore transient abort error code 1 caused by changing src
        if (audio?.error?.code === 1) {
            return;
        }

        setLoadingAudio(false);
        (audioPlayerMethods as any).onError?.();
    }

    const synchronizeVideoWithAudio = useCallback(() => {
        if (document.hidden || !isFullScreen) return;
        if (!syncEnabled || (!showVideo && !hasCanvas) || !videoBlobUrl || !youtubePlayerRef.current || !audioRef.current) return;

        const now = Date.now();
        if (now - lastSyncTime.current < 200) return;

        lastSyncTime.current = now;

        const audioTime = audioRef.current.currentTime;
        const player = youtubePlayerRef.current.getPlayer();

        if (player && typeof player.getCurrentTime === 'function') {
            try {
                const state = player.getPlayerState();

                // if player is stoped and audio playing, seek to audio time and play
                if (state === 5 && audioRef.current.paused === false) {
                    youtubePlayerRef.current.seekTo(audioTime);
                    youtubePlayerRef.current.play();
                    return;
                }

                if (state === 1 || state === 2) {
                    if (typeof player.getCurrentTime === 'function') {
                        const videoTime = player.getCurrentTime();
                        if (typeof videoTime === 'number') {
                            if (Math.abs(videoTime - audioTime) > 0.1) {
                                youtubePlayerRef.current?.seekTo(audioTime);
                            }
                        } else if (videoTime && typeof videoTime.then === 'function') {
                            videoTime.then((time: number) => {
                                if (Math.abs(time - audioTime) > 0.1) {
                                    youtubePlayerRef.current?.seekTo(audioTime);
                                }
                            }).catch(() => {
                            });
                        }
                    }
                }
            } catch (error) {
                console.error("Error al sincronizar video:", error);
            }
        }
    }, [showVideo, videoBlobUrl, syncEnabled, hasCanvas, isFullScreen]);

    useEffect(() => {
        if ((showVideo || hasCanvas) && videoBlobUrl && isFullScreen) {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
            }

            syncIntervalRef.current = setInterval(() => {
                synchronizeVideoWithAudio();
            }, 1000);
        }

        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
        };
    }, [showVideo, videoBlobUrl, synchronizeVideoWithAudio, hasCanvas, isFullScreen]);

    // Pausar video y YouTube inmediatamente al bloquear pantalla o cambiar de pestaña para evitar conflictos de MediaSession
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (youtubePlayerRef.current) {
                    youtubePlayerRef.current.pause();
                }
                if (canvasVideoRef.current) {
                    canvasVideoRef.current.pause();
                }
            } else {
                if (!paused && isFullScreen) {
                    if ((showVideo || hasCanvas) && videoBlobUrl && youtubePlayerRef.current) {
                        const currentTime = audioRef.current?.currentTime || 0;
                        youtubePlayerRef.current.seekTo(currentTime);
                        youtubePlayerRef.current.play();
                    }
                    if (canvasVideoRef.current) {
                        canvasVideoRef.current.play().catch(() => { });
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [paused, isFullScreen, showVideo, hasCanvas, videoBlobUrl]);

    const handleOnTimeUpdate = () => {
        audioPlayerMethods.onTimeUpdate();
        if (!document.hidden && isFullScreen && (showVideo || hasCanvas) && videoBlobUrl && youtubePlayerRef.current && audioRef.current) {
            synchronizeVideoWithAudio();
        }
    }

    const handlePlayAudio = () => {
        audioPlayerMethods.onPlay();
        if (!document.hidden && isFullScreen && (showVideo || hasCanvas) && videoBlobUrl && youtubePlayerRef.current) {
            const currentTime = audioRef.current?.currentTime || 0;
            youtubePlayerRef.current.seekTo(currentTime);
            youtubePlayerRef.current.play();
            console.log(youtubePlayerRef)
        }
    }

    const handleOnPause = () => {
        audioPlayerMethods.onPause();
        if ((showVideo || hasCanvas) && videoBlobUrl && youtubePlayerRef.current) {
            youtubePlayerRef.current.pause();
        }
    }

    const handleClickButton = (e: React.MouseEvent<HTMLElement>, id: string) => {
        const original = document.getElementById(id);
        if (original) {
            // Create a new event that bubbles and is cancelable
            const event = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true,
                clientX: e.clientX,
                clientY: e.clientY,
                screenX: e.screenX,
                screenY: e.screenY,
                metaKey: e.metaKey,
                ctrlKey: e.ctrlKey,
                shiftKey: e.shiftKey,
                altKey: e.altKey,
            });

            original.dispatchEvent(event);
        } else {
            console.error('btn not found: ' + id)
        }
    };

    return (
        <>
            <button
                id="current-song-options-btn"
                onClick={(e) => songOptionsModalRef.current?.open({
                    x: e.clientX,
                    y: e.clientY
                })}
                style={{
                    display: "none"
                }}>
            </button>

            {user?.uid && !offlineMode &&
                <button onClick={() => handleToggleFavourite()}
                    id="current-song-add-favs-btn"
                    className={classNames({
                        favorite: isFavourite
                    })}
                    style={{
                        display: "none"
                    }}>
                </button>
            }
            <audio ref={audioRef}
                onLoadStart={() => setLoadingAudio(true)}
                onCanPlay={() => {
                    setLoadingAudio(false);
                    (audioPlayerMethods as any).onCanPlay?.();
                }}
                onLoadedMetadata={(audioPlayerMethods as any).onLoadedMetadata}
                onError={handleAudioError}
                onEnded={handleAudioOnEnded}
                onTimeUpdate={handleOnTimeUpdate}
                onTimeUpdateCapture={handleOnTimeUpdate}
                onPlay={handlePlayAudio}
                onPause={handleOnPause}
                id="audio-player" preload="auto">
                Your browser does not support the audio element.
            </audio>
            <div id="song-player" onClick={handleClickSongPlayer}
                ref={songPlayerRef}
                className={classNames("d-flex flex-row justify-content-center align-items-center", {
                    "song-player-bot-mode": state.isBotMode
                })}>
                {hasCanvas &&
                    <div className="song-player__canvas">
                        <div className="song-player__canvas__shadow-top" />
                        <div className="song-player__canvas__shadow-bottom" />
                        {canvasUrl && <video
                            style={{
                                transition: "opacity 0.5s ease-in-out",
                                opacity: isFullScreen ? 1 : 0
                            }}
                            ref={canvasVideoRef}
                            key={canvasUrl}
                            src={canvasUrl}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="song-player__canvas__video"
                        />}
                        {songHasVideo &&
                            <YoutubeIframe
                                ref={youtubePlayerRef}
                                style={{
                                    transition: "opacity 0.5s ease-in-out",
                                    pointerEvents: "none",
                                    height: "100%",
                                    aspectRatio: 16 / 9,
                                    width: "auto",
                                    transform: "translateX(calc(-50% + 50vw))",
                                    opacity: isFullScreen ? 1 : 0
                                }}
                                className="song-player-poster-video"
                                videoId={currentSong!.id}
                            />}
                    </div>}
                <div className={classNames("song-player-outside-container", documentFullScreen ? "song-player--fullscreen" : "", hasCanvas ? "song-player--has-canvas" : "")}>
                    <div className="d-flex flex-row justify-content-start align-items-center" id="song-player-header">
                        <i id="minimizeSongPlayer"
                            title="Minimizar"
                            className="fa-solid fa-angle-down"
                            onClick={minimizeSongPlayer}></i>
                    </div>
                    <div className="song-player-info"
                        ref={songPlayerInfoRef}>
                        <div id="song-player-poster" onClick={maximizeSongPlayer}>
                            <i className="fa-solid fa-angle-up"></i>
                            <SquareImage hideOverImg={false}
                                song={currentSong || undefined}
                                big={true}
                                src={defaultIcon}
                                alt={currentSong?.name ?? "Selecciona una canción"}
                                className="song-player-poster" />
                        </div>
                        <div style={{
                            maxWidth: "100%",
                            textAlign: "left",
                            color: "white"
                        }}
                            className="song-info">
                            <div style={{
                                fontWeight: "bolder",
                                letterSpacing: "0.5px",
                                overflow: "hidden",
                                color: "white"
                            }} className="song-player-song-title">
                                {songTitle}
                            </div>
                            <div style={{
                                fontWeight: "bold",
                                letterSpacing: "0.1px",
                                overflow: "hidden",
                                color: "rgba(224,224,224,0.83)"
                            }} className="song-player-song-author">
                                {songArtist}
                            </div>
                        </div>
                    </div>
                    <div className='song-player-poster-options'>
                        <div className='song-player-poster-options__left'>
                            <button onClick={(e) => songOptionsModalRef.current?.open()}>
                                <i className="fa-solid fa-bars"></i>
                            </button>
                            {auth.currentUser && <button className={classNames('song-player-poster-options__fav-btn', {
                                'is-favorite': isFavourite
                            })} onClick={handleToggleFavourite}>
                                <i className="fa-solid fa-heart-broken"></i>
                                <i className="fa-solid fa-heart"></i>
                                <i className="fa-regular fa-heart"></i>
                            </button>}
                            <button onClick={(e) => handleClickButton(e, 'current-song-copy-link')}>
                                <i className="fa-solid fa-share-nodes"></i>
                            </button>
                        </div>
                    </div>
                    <div className="player-controls" ref={controlsRef}>
                        <i className={classNames("fa-solid fa-shuffle song-player-control-terciary", { "song-player-control-active": randomQueue })}
                            id="random-queue-button" onClick={() => handleSetRandomQueue(!randomQueue)}></i>
                        <i className="fa-solid fa-backward-step song-player-control-secondary"
                            onClick={playerControls.prevTrack}
                            id="prev-track-button"></i>
                        <i className="fa-solid fa-circle-play song-player-control"
                            hidden={!paused}
                            onClick={playerControls.playTrack}
                            id="play-song-button"></i>
                        <i className="fa-solid fa-circle-pause song-player-control"
                            hidden={paused || (state.isBotMode ? false : loadingAudio)}
                            onClick={playerControls.stopTrack}
                            id="stop-song-button"></i>
                        <i className="fa-solid fa-spinner fa-spin song-player-control"
                            hidden={paused || !(state.isBotMode ? false : loadingAudio)}
                            id="stop-song-button"></i>
                        <i className="fa-solid fa-forward-step song-player-control-secondary"
                            onClick={playerControls.nextTrack}
                            id="next-track-button"></i>
                        <i className={classNames("fa-solid fa-repeat song-player-control-terciary", { "song-player-control-active": loopMode !== "none" })}
                            onClick={handleChangeLoopMode} id="loop-queue-button">
                            {loopMode === 'song' && <span>1</span>}
                        </i>
                    </div>
                    <div className="d-flex flex-row align-items-center" id="time-progress">
                        <ProgressSlider ref={timeSlider} onChange={handleChangeTime} id="timeSlider" min={0} max={100} />
                    </div>
                    <div className="durationTimeContainer">
                        <div id="currentTime">
                            {currentTime}
                        </div>
                        <div id="songDuration">
                            {duration}
                        </div>
                    </div>
                    <div className="d-flex flex-row justify-content-end align-items-center extra-control-button"
                        id="player-song-extra-controls">
                        {!offlineMode && <i id="current-song-copy-link" className="fa-solid fa-share-nodes song-player-share extra-control-button"
                            title="Copiar url" onClick={handleCopyLink}></i>}
                        <div className="extra-control-button" onClick={handleOpenQueueModal}>
                            <span
                                className="extra-control-button__queue-counter badge rounded-pill bg-light text-dark">{songQueue.length}</span>
                            <i className="fa-solid fa-bars" title="Cola reproducción">
                            </i>
                        </div>
                        {!isMobileDevice() && <>
                            <i id="song-player-volume-icon"
                                // onClick={toggleMute}
                                ref={volumeIconRef} style={{ width: "43px", marginRight: "2px" }}
                                className="fa-solid fa-volume-low extra-control-button"></i>
                            <div className="col-6 d-flex flex-row align-items-center">
                                <ProgressSlider onChange={handleVolumeChange} id="volumeSlider"
                                    ref={volumeSliderRef} modifyOnScroll={true} />
                            </div>
                        </>}
                    </div>
                    <SongOptionsModal song={currentSong ?? null} ref={songOptionsModalRef} scrollRef={songOptionsModalRef}
                        setSong={(song: Song) => {
                            setIsFavourite(song.is_favorite);
                        }} />
                    <SongQueueScrollModal ref={songQueueModalRef} />
                </div>
            </div>
        </>
    )
}

export default AudioPlayerView;
